// frontend/src/contexts/NotificationContext.jsx
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { API_URL } from '../config/api';
import socketService from '../services/SocketService';
import notificationNavigation from '../utils/NotificationNavigation';
import { shouldShowToast, markToastAsShown, getNotificationPriority } from '../utils/NotificationPriorityManager';

/**
 * 🔔 CONTEXT DE NOTIFICAÇÕES PARA COMISSÕES
 * 
 * Este context gerencia todas as notificações do sistema,
 * incluindo notificações de comissões, vendas e ações importantes.
 */

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications deve ser usado dentro de NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);

  // Tipos de notificação
  const NotificationTypes = {
    COMMISSION_RECEIVED: 'commission_received',
    COMMISSION_AVAILABLE: 'commission_available',
    SALE_PROCESSED: 'sale_processed',
    AFFILIATE_SIGNUP: 'affiliate_signup',
    PAYMENT_RELEASED: 'payment_released',
    SYSTEM_UPDATE: 'system_update',
    WARNING: 'warning',
    ERROR: 'error',
    INFO: 'info',
    SUCCESS: 'success'
  };

  // Configurações visuais por tipo
  const getNotificationConfig = (type) => {
    const configs = {
      [NotificationTypes.COMMISSION_RECEIVED]: {
        icon: 'mdi:currency-brl',
        color: 'success',
        bgColor: 'bg-success-subtle',
        title: 'Nova Comissão',
        sound: 'commission'
      },
      [NotificationTypes.COMMISSION_AVAILABLE]: {
        icon: 'mdi:wallet',
        color: 'info',
        bgColor: 'bg-info-subtle',
        title: 'Comissão Disponível',
        sound: 'payment'
      },
      [NotificationTypes.SALE_PROCESSED]: {
        icon: 'mdi:cart-check',
        color: 'primary',
        bgColor: 'bg-primary-subtle',
        title: 'Venda Processada',
        sound: 'sale'
      },
      [NotificationTypes.AFFILIATE_SIGNUP]: {
        icon: 'mdi:account-plus',
        color: 'warning',
        bgColor: 'bg-warning-subtle',
        title: 'Novo Afiliado',
        sound: 'notification'
      },
      [NotificationTypes.PAYMENT_RELEASED]: {
        icon: 'mdi:bank-transfer',
        color: 'success',
        bgColor: 'bg-success-subtle',
        title: 'Pagamento Liberado',
        sound: 'payment'
      },
      [NotificationTypes.SYSTEM_UPDATE]: {
        icon: 'mdi:update',
        color: 'info',
        bgColor: 'bg-info-subtle',
        title: 'Atualização',
        sound: 'notification'
      },
      [NotificationTypes.WARNING]: {
        icon: 'mdi:alert',
        color: 'warning',
        bgColor: 'bg-warning-subtle',
        title: 'Atenção',
        sound: 'warning'
      },
      [NotificationTypes.ERROR]: {
        icon: 'mdi:alert-circle',
        color: 'danger',
        bgColor: 'bg-danger-subtle',
        title: 'Erro',
        sound: 'error'
      },
      [NotificationTypes.INFO]: {
        icon: 'mdi:information',
        color: 'info',
        bgColor: 'bg-info-subtle',
        title: 'Informação',
        sound: 'notification'
      },
      [NotificationTypes.SUCCESS]: {
        icon: 'mdi:check-circle',
        color: 'success',
        bgColor: 'bg-success-subtle',
        title: 'Sucesso',
        sound: 'success'
      }
    };

    return configs[type] || configs[NotificationTypes.INFO];
  };

  // Reproduzir som (opcional)
  const playSound = useCallback((soundType) => {
    try {
      const audio = new Audio(`/sounds/${soundType}.mp3`);
      audio.volume = 0.3;
      audio.play().catch(() => {
        // Silenciar erro se som não estiver disponível
      });
    } catch (error) {
      // Silenciar erro de som
    }
  }, []);

  // Adicionar notificação
  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const config = getNotificationConfig(notification.type);
    
    const newNotification = {
      id,
      type: notification.type,
      title: notification.title || config.title,
      message: notification.message,
      data: notification.data || {},
      timestamp: new Date(),
      read: false,
      persistent: notification.persistent || false,
      autoHide: notification.autoHide !== false, // padrão true
      hideDelay: notification.hideDelay || 5000,
      onClick: notification.onClick,
      actions: notification.actions || [],
      ...config
    };

    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);

    // Reproduzir som se habilitado
    if (localStorage.getItem('notifications_sound') !== 'false') {
      playSound(config.sound);
    }

    // Auto-hide para notificações não persistentes
    if (newNotification.autoHide && !newNotification.persistent) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.hideDelay);
    }

    return id;
  }, [playSound]);

  // Remover notificação
  const removeNotification = useCallback((id) => {
    setNotifications(prev => {
      const notification = prev.find(n => n.id === id);
      if (notification && !notification.read) {
        setUnreadCount(count => Math.max(0, count - 1));
      }
      return prev.filter(n => n.id !== id);
    });
  }, []);

  // Marcar como lida
  const markAsRead = useCallback(async (id) => {
    try {
      // Atualizar estado local imediatamente
      setNotifications(prev => prev.map(notification => {
        if (notification.id === id && !notification.read) {
          setUnreadCount(count => Math.max(0, count - 1));
          return { ...notification, read: true, read_at: new Date().toISOString() };
        }
        return notification;
      }));
      
      // Chamar API para marcar como lida no backend
      const response = await fetch(`${API_URL}/api/notifications/${id}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao marcar notificação como lida');
      }
      
      console.log(`✅ Notificação ${id} marcada como lida`);
    } catch (error) {
      console.error('❌ Erro ao marcar notificação como lida:', error);
    }
  }, []);

  // Marcar todas como lidas
  const markAllAsRead = useCallback(async () => {
    try {
      // Atualizar estado local imediatamente
      setNotifications(prev => prev.map(notification => ({ 
        ...notification, 
        read: true, 
        read_at: new Date().toISOString() 
      })));
      setUnreadCount(0);
      
      // Chamar API para marcar todas como lidas no backend
      const response = await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao marcar todas as notificações como lidas');
      }
      
      const result = await response.json();
      console.log(`✅ ${result.data.updatedCount} notificações marcadas como lidas`);
    } catch (error) {
      console.error('❌ Erro ao marcar todas as notificações como lidas:', error);
    }
  }, []);

  // Excluir notificação específica
  const deleteNotification = useCallback(async (id) => {
    try {
      // Remover do estado local imediatamente
      setNotifications(prev => {
        const notification = prev.find(n => n.id === id);
        if (notification && !notification.read) {
          setUnreadCount(count => Math.max(0, count - 1));
        }
        return prev.filter(n => n.id !== id);
      });
      
      // Chamar API para excluir no backend
      const response = await fetch(`${API_URL}/api/notifications/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao excluir notificação');
      }
      
      console.log(`✅ Notificação ${id} excluída com sucesso`);
    } catch (error) {
      console.error('❌ Erro ao excluir notificação:', error);
    }
  }, []);

  // Limpar todas
  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Limpar apenas lidas
  const clearRead = useCallback(() => {
    setNotifications(prev => prev.filter(n => !n.read));
  }, []);

  // Métodos de conveniência para tipos específicos
  const notifyCommissionReceived = useCallback((amount, productName, affiliateName) => {
    return addNotification({
      type: NotificationTypes.COMMISSION_RECEIVED,
      message: `Você recebeu R$ ${(amount / 100).toFixed(2)} de comissão do produto "${productName}"${affiliateName ? ` via ${affiliateName}` : ''}`,
      data: { amount, productName, affiliateName },
      persistent: true
    });
  }, [addNotification]);

  const notifyCommissionAvailable = useCallback((amount, availableDate) => {
    return addNotification({
      type: NotificationTypes.COMMISSION_AVAILABLE,
      message: `R$ ${(amount / 100).toFixed(2)} em comissões estão disponíveis para saque`,
      data: { amount, availableDate },
      persistent: true
    });
  }, [addNotification]);

  const notifySaleProcessed = useCallback((saleAmount, productName, commissionAmount) => {
    return addNotification({
      type: NotificationTypes.SALE_PROCESSED,
      message: `Nova venda de R$ ${(saleAmount / 100).toFixed(2)} - Sua comissão: R$ ${(commissionAmount / 100).toFixed(2)}`,
      data: { saleAmount, productName, commissionAmount }
    });
  }, [addNotification]);

  const notifyAffiliateSignup = useCallback((affiliateName, productName) => {
    return addNotification({
      type: NotificationTypes.AFFILIATE_SIGNUP,
      message: `${affiliateName} solicitou para afiliar-se ao produto "${productName}"`,
      data: { affiliateName, productName },
      persistent: true
    });
  }, [addNotification]);

  const notifyPaymentReleased = useCallback((amount, method) => {
    return addNotification({
      type: NotificationTypes.PAYMENT_RELEASED,
      message: `Pagamento de R$ ${(amount / 100).toFixed(2)} foi processado via ${method}`,
      data: { amount, method },
      persistent: true
    });
  }, [addNotification]);

  const notifySuccess = useCallback((message, data = {}) => {
    return addNotification({
      type: NotificationTypes.SUCCESS,
      message,
      data
    });
  }, [addNotification]);

  const notifyError = useCallback((message, data = {}) => {
    return addNotification({
      type: NotificationTypes.ERROR,
      message,
      data,
      persistent: true
    });
  }, [addNotification]);

  const notifyWarning = useCallback((message, data = {}) => {
    return addNotification({
      type: NotificationTypes.WARNING,
      message,
      data,
      persistent: true
    });
  }, [addNotification]);

  const notifyInfo = useCallback((message, data = {}) => {
    return addNotification({
      type: NotificationTypes.INFO,
      message,
      data
    });
  }, [addNotification]);

  // 🔌 Conexão real com Socket.IO (sem mock)
  useEffect(() => {
    const connect = () => {
      setIsConnected(true);
      console.log('🔔 Sistema de notificações conectado (100% real)');
      
      return () => {
        setIsConnected(false);
      };
    };

    const cleanup = connect();
    return cleanup;
  }, []);

  // 🔌 Sistema híbrido: Socket.IO + Polling automático
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      console.log('🔔 Sem token - pulando conexão Socket.IO');
      return;
    }

    let pollingInterval = null;
    let lastNotificationId = null;

    // Função para carregar notificações via API
    const loadNotifications = async () => {
      try {
        const response = await fetch(`${API_URL}/api/notifications`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const newNotifications = data.notifications || [];
          
          console.log('🔄 Polling executado - Notificações encontradas:', newNotifications.length);
          
          // Verificar se há novas notificações
          if (newNotifications.length > 0) {
            const latestNotification = newNotifications[0];
            
            if (lastNotificationId === null) {
              // Primeira carga - não mostrar toasts
              console.log('🔄 Primeira carga - definindo lastNotificationId:', latestNotification.id);
              lastNotificationId = latestNotification.id;
            } else if (latestNotification.id !== lastNotificationId) {
              // Nova notificação detectada!
              console.log('🆕 Nova notificação detectada via polling:', latestNotification);
              console.log('🆕 ID anterior:', lastNotificationId, 'ID atual:', latestNotification.id);
              
              // Adicionar ao estado
              setNotifications(prev => [latestNotification, ...prev]);
              setUnreadCount(prev => prev + 1);
              
              // Verificar se deve mostrar toast
              const showToast = shouldShowToast(latestNotification);
              if (showToast && window.showNotificationToast) {
                window.showNotificationToast(latestNotification);
                markToastAsShown(latestNotification.id);
              }
              
              lastNotificationId = latestNotification.id;
            } else {
              console.log('🔄 Nenhuma nova notificação - ID atual:', latestNotification.id);
            }
          }
        }
      } catch (error) {
        console.log('⚠️ Erro ao carregar notificações via polling:', error.message);
      }
    };

    // Conectar ao Socket.IO
    socketService.connect(token);

    // Listener para notificações via Socket.IO
    const handleSocketNotification = (event) => {
      const notification = event.detail;
      console.log('📢 Notificação recebida via Socket.IO:', notification);
      
      // Adicionar notificação ao estado
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Verificar se deve mostrar toast
      const showToast = shouldShowToast(notification);
      if (showToast && window.showNotificationToast) {
        window.showNotificationToast(notification);
        markToastAsShown(notification.id);
      }
    };

    // Adicionar listener do Socket.IO
    window.addEventListener('socketNotification', handleSocketNotification);

    // Iniciar polling como fallback (a cada 10 segundos) - PAUSADO PARA TESTE
    console.log('🔄 Polling PAUSADO para testar Socket.IO puro...');
    // pollingInterval = setInterval(loadNotifications, 10000);

    // Cleanup - IMPORTANTE: Não desconectar o Socket.IO no cleanup!
    // O Socket.IO deve permanecer conectado durante toda a sessão do usuário
    return () => {
      window.removeEventListener('socketNotification', handleSocketNotification);
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      // NÃO chamar socketService.disconnect() aqui para manter a conexão persistente
    };
  }, []);

  // Carregar notificações reais do banco de dados
  useEffect(() => {
    let isMounted = true;
    
    const loadNotifications = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token || !isMounted) {
          console.log('🔔 Sem token ou componente desmontado - pulando carregamento de notificações');
          return;
        }

        // Não carregar notificações em páginas públicas (checkout, login, etc.)
        const publicPaths = ['/checkout', '/login', '/sign-up', '/forgot-password'];
        const currentPath = window.location.hash.replace('#', '');
        const isPublicPage = publicPaths.some(path => currentPath.startsWith(path));
        
        if (isPublicPage || !isMounted) {
          console.log('🔔 Página pública ou componente desmontado - pulando carregamento de notificações');
          return;
        }

        const response = await fetch(`${API_URL}/api/notifications`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.notifications) {
            console.log(`📥 Carregadas ${data.notifications.length} notificações do servidor`);
            
            // Carregar notificações do servidor (APENAS no dropdown, SEM toasts)
            const serverNotifications = data.notifications.map(notification => ({
              ...notification,
              timestamp: new Date(notification.created_at || notification.timestamp),
              fromServer: true, // 🚫 Não mostrar toast para notificações carregadas do servidor
              ...getNotificationConfig(notification.type)
            }));
            
            setNotifications(serverNotifications);
            setUnreadCount(serverNotifications.filter(n => !n.read).length);
            
            console.log(`✅ ${serverNotifications.length} notificações carregadas no dropdown (sem toasts)`);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar notificações:', error);
      }
    };

    loadNotifications();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // 🧭 Funcionalidades de navegação
  const navigateToNotification = useCallback((notification) => {
    notificationNavigation.navigateToNotification(notification);
  }, []);

  const getNotificationUrl = useCallback((notification) => {
    return notificationNavigation.getNavigationUrl(notification);
  }, []);

  const getNotificationActions = useCallback((notification) => {
    return notificationNavigation.getNotificationActions(notification);
  }, []);

  // 🔧 Inicializar navegação quando necessário
  useEffect(() => {
    // Esta função será chamada quando o navigate estiver disponível
    const initializeNavigation = (navigate) => {
      notificationNavigation.initialize(navigate);
    };

    // Expor função para inicialização externa
    window.initializeNotificationNavigation = initializeNavigation;
  }, []);

  const value = {
    // Estado
    notifications,
    unreadCount,
    isConnected,
    
    // Ações
    addNotification,
    removeNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    clearRead,
    
    // 🧭 Navegação
    navigateToNotification,
    getNotificationUrl,
    getNotificationActions,
    
    // Métodos de conveniência
    notifyCommissionReceived,
    notifyCommissionAvailable,
    notifySaleProcessed,
    notifyAffiliateSignup,
    notifyPaymentReleased,
    notifySuccess,
    notifyError,
    notifyWarning,
    notifyInfo,
    
    // Tipos
    NotificationTypes,
    getNotificationConfig
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};















